/**
 * StudentFormModal — create or edit a student with inline guardian rows.
 *
 * Uses react-hook-form for controlled validation. Backend field errors
 * (from the 422 error body's `details`) are mapped back to individual
 * field-level messages after submission.
 */

import React from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Modal, Button, Input } from '@/components/common';
import {
  useCreateStudent,
  useUpdateStudent,
  type StudentCreate,
  type StudentUpdate,
  type StudentResponse,
  type GuardianInline,
} from '@features/people/api/students';
import type { ApiError } from '@core/api/client';

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: ${({ theme }) => `${theme.spacing.lg} 0 ${theme.spacing.md}`};
`;

const GuardianRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  position: relative;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.errorLight};
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  margin-top: 2px;
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

// ---------------------------------------------------------------------------
// Form shape
// ---------------------------------------------------------------------------

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  gender: string;
  notes: string;
  branch_id: string;
  tags: string;
  guardians: GuardianInline[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface StudentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When provided, the modal operates in edit mode. */
  student?: StudentResponse;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const StudentFormModal: React.FC<StudentFormModalProps> = ({
  isOpen,
  onClose,
  student,
}) => {
  const isEdit = Boolean(student);

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      first_name: student?.first_name ?? '',
      last_name: student?.last_name ?? '',
      email: student?.email ?? '',
      phone: student?.phone ?? '',
      date_of_birth: student?.date_of_birth ?? '',
      gender: student?.gender ?? '',
      notes: student?.notes ?? '',
      branch_id: student?.branch_id ?? '',
      tags: student?.tags?.join(', ') ?? '',
      guardians: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'guardians' });

  const createMutation = useCreateStudent();
  const updateMutation = useUpdateStudent();

  const handleClose = () => {
    reset();
    onClose();
  };

  const mapBackendErrors = (err: ApiError) => {
    // Backend returns details as an array of { loc, msg } for 422
    if (err.status === 422 && Array.isArray(err.details)) {
      (err.details as { loc: (string | number)[]; msg: string }[]).forEach(
        ({ loc, msg }) => {
          const field = loc[loc.length - 1] as keyof FormValues;
          if (field) {
            setError(field, { type: 'server', message: msg });
          }
        },
      );
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    const tagsArray = values.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      if (isEdit && student) {
        const body: StudentUpdate = {
          first_name: values.first_name || undefined,
          last_name: values.last_name || undefined,
          email: values.email || null,
          phone: values.phone || null,
          date_of_birth: values.date_of_birth || null,
          gender: (values.gender as StudentUpdate['gender']) || null,
          notes: values.notes || null,
          branch_id: values.branch_id || null,
          tags: tagsArray,
        };
        await updateMutation.mutateAsync({ id: student.id, body });
        toast.success('Student updated');
      } else {
        const body: StudentCreate = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email || null,
          phone: values.phone || null,
          date_of_birth: values.date_of_birth || null,
          gender: (values.gender as StudentCreate['gender']) || null,
          notes: values.notes || null,
          branch_id: values.branch_id || null,
          tags: tagsArray,
          guardians: values.guardians,
        };
        await createMutation.mutateAsync(body);
        toast.success('Student created');
      }
      handleClose();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 422) {
        mapBackendErrors(apiErr);
      }
      // Non-422 errors are handled globally in apiCall (toast.error).
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit Student' : 'Add New Student'}
      size="lg"
      data-testid="candeza-students-form-modal"
      data-flow="student-form-modal"
    >
      <form onSubmit={onSubmit} noValidate>
        <FormGrid>
          <div>
            <Input
              label="First Name *"
              placeholder="First name"
              data-testid="candeza-students-form-firstname-field"
              data-flow="student-name-input"
              {...register('first_name', { required: 'First name is required' })}
            />
            {errors.first_name && <ErrorText>{errors.first_name.message}</ErrorText>}
          </div>
          <div>
            <Input
              label="Last Name *"
              placeholder="Last name"
              data-testid="candeza-students-form-lastname-field"
              {...register('last_name', { required: 'Last name is required' })}
            />
            {errors.last_name && <ErrorText>{errors.last_name.message}</ErrorText>}
          </div>
          <div>
            <Input
              label="Email"
              type="email"
              placeholder="student@example.com"
              {...register('email')}
            />
          </div>
          <div>
            <Input
              label="Phone"
              type="tel"
              placeholder="+91 98765 43210"
              {...register('phone')}
            />
          </div>
          <div>
            <Input
              label="Date of Birth"
              type="date"
              data-testid="candeza-students-form-dob-field"
              {...register('date_of_birth')}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Gender</label>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <select
                  {...field}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', fontSize: '14px' }}
                >
                  <option value="">Select gender...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              )}
            />
          </div>
          <div>
            <Input
              label="Branch ID"
              placeholder="e.g. main"
              {...register('branch_id')}
            />
          </div>
          <div>
            <Input
              label="Tags (comma-separated)"
              placeholder="guitar, beginner"
              {...register('tags')}
            />
          </div>
          <FullWidth>
            <Input
              label="Notes"
              placeholder="Any additional notes..."
              {...register('notes')}
            />
          </FullWidth>
        </FormGrid>

        {/* Inline guardian rows — only shown for create */}
        {!isEdit && (
          <>
            <SectionTitle>Guardians</SectionTitle>
            <div data-testid="candeza-guardians-section">
              {fields.map((field, index) => (
                <GuardianRow key={field.id} data-testid={`candeza-guardians-row-${field.id}`}>
                  <RemoveButton
                    type="button"
                    onClick={() => remove(index)}
                    aria-label="Remove guardian"
                  >
                    ✕
                  </RemoveButton>
                  <div>
                    <Input
                      label="Guardian First Name *"
                      placeholder="First name"
                      {...register(`guardians.${index}.first_name`, {
                        required: 'Required',
                      })}
                    />
                    {errors.guardians?.[index]?.first_name && (
                      <ErrorText>{errors.guardians[index]?.first_name?.message}</ErrorText>
                    )}
                  </div>
                  <div>
                    <Input
                      label="Guardian Last Name *"
                      placeholder="Last name"
                      {...register(`guardians.${index}.last_name`, {
                        required: 'Required',
                      })}
                    />
                  </div>
                  <div>
                    <Input
                      label="Phone *"
                      type="tel"
                      placeholder="+91 98765 43210"
                      {...register(`guardians.${index}.phone`, {
                        required: 'Phone is required',
                      })}
                    />
                    {errors.guardians?.[index]?.phone && (
                      <ErrorText>{errors.guardians[index]?.phone?.message}</ErrorText>
                    )}
                  </div>
                  <div>
                    <Input
                      label="Email"
                      type="email"
                      placeholder="parent@example.com"
                      {...register(`guardians.${index}.email`)}
                    />
                  </div>
                </GuardianRow>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="candeza-students-form-guardian-add-btn"
              onClick={() =>
                append({
                  first_name: '',
                  last_name: '',
                  phone: '',
                  email: null,
                  relationship: 'guardian',
                  is_primary: fields.length === 0,
                })
              }
            >
              + Add Guardian
            </Button>
          </>
        )}

        <ModalActions>
          <Button type="button" variant="outline" onClick={handleClose} data-testid="candeza-students-form-cancel-btn">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} data-testid="candeza-students-form-save-btn" data-flow="student-save-btn">
            {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Student'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};

